import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  Spinner,
} from "reactstrap";
import { EqTimeSlots } from "../Dashboard/DashboardEquipment/EqTimeSLot";
import {
  getLatestEqTimeSlot,
  getNextEqTimeSlot,
} from "../../utils/EqTimeSlotUtils";
import {
  addMinutes,
  extractDate,
  extractTime,
  formatter,
  getJSTDateStr,
} from "../../utils/dateformatter";
import { eventInfo } from "../../services/EventApi";
import { formatKey, reverseFormatKey } from "../../utils/formatKey";
import { toast } from "react-toastify";
import {
  mapEventInfoToGroups,
  microAllocations,
  microCustomAllocations,
  microgridData,
  renderGroups,
  townAllocations,
  townHallData,
} from "./equipGroup";

const EquipmentModal = ({
  isOpen,
  toggle,
  fetchEventList,
  data,
  modalPos,
  arrowTop,
}) => {
  const today = getJSTDateStr();
  const tomorrow = getJSTDateStr(1);
  const isEditing = !!data?.event_id;
  const maxDays = getJSTDateStr(90);
  const textareaRef = useRef(null);

  const [startTime, setStartTime] = useState(getLatestEqTimeSlot(today));
  const [endTime, setEndTime] = useState(getNextEqTimeSlot(today));

  const [showStartDropdown, setShowStartDropdown] = useState(false);
  const [showEndDropdown, setShowEndDropdown] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [formValues, setFormValues] = useState({
    eventName: "",
    eventDetail: "",
    startDate: today,
    endDate: today,
  });
  const [isChanged, setIsChanged] = useState(false);
  const [initialMicroGroups, setInitialMicroGroups] = useState([]);
  const [initialTownGroups, setInitialTownGroups] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setIsChanged(false);
    }
  }, [isEditing, data]);
  useEffect(() => {
    if (data) {
      setFormValues({
        eventName: data.title || "",
        eventDetail: data.description || "",
        startDate: data.start_time ? extractDate(data.start_time) : today,
        endDate: data.end_time ? extractDate(data.end_time) : tomorrow,
      });

      setEndTime(
        data?.end_time ? extractTime(data?.end_time) : getNextEqTimeSlot(today),
      );
      setStartTime(
        data?.start_time
          ? extractTime(data?.start_time)
          : getLatestEqTimeSlot(today),
      );

      if (data?.event_info) {
        const micro = mapEventInfoToGroups(
          microgridData,
          data.event_info.microgrid,
        );
        const town = mapEventInfoToGroups(townHallData, data.event_info.yakuba);

        setMicroGroups(micro);
        setTownGroups(town);

        // store initial groups for change detection
        setInitialMicroGroups(micro);
        setInitialTownGroups(town);

        // mark initial data as loaded
        setInitialLoaded(true);
      }
    }
  }, [data]);

  // useEffect(() => {
  //   if (data) {
  //     setFormValues({
  //       eventName: data.title || "",
  //       eventDetail: data.description || "",
  //       startDate: data.start_time ? extractDate(data.start_time) : today,
  //       endDate: data.end_time ? extractDate(data.end_time) : tomorrow,
  //     });
  //   }
  //   setEndTime(
  //     data?.end_time ? extractTime(data?.end_time) : getNextEqTimeSlot(today),
  //   );
  //   setStartTime(
  //     data?.start_time
  //       ? extractTime(data?.start_time)
  //       : getLatestEqTimeSlot(today),
  //   );

  //   if (data?.event_info) {
  //     setMicroGroups(
  //       mapEventInfoToGroups(microgridData, data.event_info.microgrid),
  //     );
  //     setTownGroups(mapEventInfoToGroups(townHallData, data.event_info.yakuba));
  //   }
  // }, [data]);

  useEffect(() => {
    if (formValues.endDate < formValues.startDate) {
      setFormValues((prev) => ({
        ...prev,
        endDate: prev.startDate,
      }));
    }

    if (formValues.startDate === formValues.endDate && !(startTime < endTime)) {
      const newEndTime = addMinutes(startTime, 30);
      setEndTime(newEndTime);
    }
  }, [formValues.startDate, formValues.endDate, startTime]);

  useEffect(() => {
    if (!isEditing) {
      if (formValues.startDate !== formValues.endDate) {
        setEndTime("00:00");
      }
    }
  }, [formValues.endDate]);

  useEffect(() => {
    if (!isEditing) {
      if (formValues.startDate > today) {
        setStartTime("00:00");
        setEndTime("00:30");
      } else {
        setStartTime(getLatestEqTimeSlot(today));
        setEndTime(getNextEqTimeSlot(today));
      }
    }
  }, [formValues.startDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (isEditing) setIsChanged(true);
  };

  const startDropdownRef = useRef(null);
  const endDropdownRef = useRef(null);
  const resetAll = () => {
    setMicroGroups((prev) =>
      prev.map((group) => ({
        ...group,
        all: true,
        states: group.states.map(() => true),
      })),
    );

    setTownGroups((prev) =>
      prev.map((group) => ({
        ...group,
        all: true,
        states: group.states.map(() => true),
      })),
    );

    setFormValues((prev) => ({
      eventName: "",
      eventDetail: "",
      startDate: today,
      endDate: today,
    }));
    setStartTime(getLatestEqTimeSlot(today));
    setEndTime(getNextEqTimeSlot(today));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (showStartDropdown || showEndDropdown) &&
        (startDropdownRef.current || endDropdownRef.current) &&
        !(startDropdownRef.current || endDropdownRef.current).contains(
          event.target,
        )
      ) {
        setShowStartDropdown(false);
        setShowEndDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showStartDropdown, showEndDropdown]);

  const handleCancel = () => {
    resetAll();
    toggle();
  };

  useEffect(() => {
    if (isOpen && !isEditing) {
      resetAll();
    }
  }, [isOpen, isEditing]);

  const initializeGroups = (data) =>
    data.map((g) => ({ ...g, all: true, states: g.items.map(() => true) }));

  const [microGroups, setMicroGroups] = useState(() =>
    initializeGroups(microgridData),
  );
  const [townGroups, setTownGroups] = useState(() =>
    initializeGroups(townHallData),
  );

  const buildEventInfo = () => {
    const microgrid = {};
    microGroups.forEach((group) => {
      group.items.forEach((item, i) => {
        if (!group.states[i]) {
          microgrid[formatKey(item)] = false;
        }
      });
    });

    const yakuba = {};
    townGroups.forEach((group) => {
      group.items.forEach((item, i) => {
        if (!group.states[i]) {
          yakuba[formatKey(item)] = false;
        }
      });
    });

    return { microgrid: microgrid, yakuba: yakuba };
  };

  const eventData = buildEventInfo?.() || { microgrid: {}, yakuba: {} };

  const noChangeinEvent =
    !Object.keys(eventData.microgrid || {}).length &&
    !Object.keys(eventData.yakuba || {}).length;

  const noData =
    !formValues.eventName || !formValues.eventDetail || noChangeinEvent;

  const addEvent = async () => {
    setIsAdding(true);
    try {
      const payload = {
        request_type: isEditing ? "update" : "add_event",
        ...(isEditing && { event_id: data.event_id }),
        plant_id: "MG_PLANT_1",
        end_datetime: formatter(formValues.endDate) + " " + endTime + ":00",
        start_datetime:
          formatter(formValues.startDate) + " " + startTime + ":00",
        event_name: formValues.eventName,
        description: formValues.eventDetail,
        event_info: buildEventInfo(),
      };

      const response = await eventInfo(payload);
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setIsAdding(false);
      resetAll();
      toggle();
    }
  };

  const handleAdd = async () => {
    await addEvent();
    await fetchEventList();
    toggle();
  };

  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto"; // reset
      el.style.height = el.scrollHeight + "px"; // resize
    }
  }, [formValues.eventDetail]);
  const groupsChanged = (current, initial) => {
    if (current.length !== initial.length) return true;

    for (let i = 0; i < current.length; i++) {
      for (let j = 0; j < current[i].states.length; j++) {
        if (current[i].states[j] !== initial[i].states[j]) return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (isEditing) {
      const microChanged = groupsChanged(microGroups, initialMicroGroups);
      const townChanged = groupsChanged(townGroups, initialTownGroups);

      if (microChanged || townChanged) {
        setIsChanged(true);
      } else {
        setIsChanged(false);
      }
    }
  }, [microGroups, townGroups]);

  return (
    <Modal
      centered
      isOpen={isOpen}
      size="lg"
      className="equipmentModal equipmentViewModal"
      style={
        isEditing
          ? {
              position: "absolute",
              left: modalPos?.left,
              "--arrow-top": `${arrowTop - 30}px`,
            }
          : undefined
      }
    >
      <ModalBody className="modalPadding">
        <Row className="allRow">
          <Col xs={3}>
            <label className="label">１. イベント日時</label>
          </Col>
          <Col xs={7} className="d-flex px-0">
            <Row className=" dateTime  " style={{ position: "relative" }}>
              <Col xs={8} className=" dtCol">
                <Input
                  className="inputDate"
                  id="exampleDateStart"
                  type="date"
                  value={formValues.startDate}
                  name="startDate"
                  min={today}
                  max={maxDays}
                  onChange={handleChange}
                  onClick={() =>
                    document.getElementById("exampleDateStart").showPicker?.()
                  }
                />

                <img
                  src="./Calendar.png"
                  className=" calendarImg ms-1 "
                  onClick={() =>
                    document.getElementById("exampleDateStart").showPicker?.()
                  }
                />
              </Col>

              <Col
                xs={4}
                className="dtCol"
                onClick={() => setShowStartDropdown((prev) => !prev)}
              >
                <Input
                  readOnly
                  className="inputTime"
                  type="text"
                  value={startTime}
                  placeholder="hh:mm"
                />

                <FontAwesomeIcon icon={faChevronDown} className="down" />
                {showStartDropdown && (
                  <div
                    ref={startDropdownRef}
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: "0",
                      background: "white",
                      border: "1px solid #ccc",
                      zIndex: 1000,
                    }}
                  >
                    <EqTimeSlots
                      selectedTime={startTime}
                      selectedDate={formValues.startDate}
                      latestTime={getLatestEqTimeSlot(today)}
                      onSelect={(time) => {
                        setStartTime(time);
                        // setShowStartDropdown(false);
                        if (isEditing) setIsChanged(true);
                      }}
                    />
                  </div>
                )}
              </Col>
            </Row>
            <span className="midDate">~</span>
            <Row className=" dateTime  " style={{ position: "relative" }}>
              <Col xs={8} className=" dtCol">
                <Input
                  className="inputDate"
                  id="exampleDateEnd"
                  name="endDate"
                  type="date"
                  min={formValues.startDate}
                  max={maxDays}
                  value={formValues.endDate}
                  onChange={handleChange}
                  onClick={() =>
                    document.getElementById("exampleDateEnd").showPicker?.()
                  }
                />

                <img
                  src="./Calendar.png"
                  className=" calendarImg ms-1 "
                  onClick={() =>
                    document.getElementById("exampleDateEnd").showPicker?.()
                  }
                />
              </Col>

              <Col
                xs={4}
                className="dtCol"
                onClick={() => setShowEndDropdown((prev) => !prev)}
              >
                <Input
                  readOnly
                  className="inputTime "
                  type="text"
                  value={endTime}
                  placeholder="hh:mm"
                />

                <FontAwesomeIcon icon={faChevronDown} className="down" />
                {showEndDropdown && (
                  <div
                    ref={endDropdownRef}
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: "0",
                      background: "white",
                      border: "1px solid #ccc",
                      zIndex: 1000,
                    }}
                  >
                    <EqTimeSlots
                      selectedTime={endTime}
                      startDate={formValues.startDate}
                      startTime={startTime}
                      selectedDate={formValues.endDate}
                      latestTime={getNextEqTimeSlot(today)}
                      onSelect={(time) => {
                        setEndTime(time);
                        if (isEditing) setIsChanged(true);
                      }}
                    />
                  </div>
                )}
              </Col>
            </Row>
          </Col>
          <Col xs={2} className="impCol">
            <span className="imp">必須</span>
          </Col>
        </Row>

        <Row className="allRow">
          <Col xs={3}>
            <label className="label">2. イベント名</label>
          </Col>
          <Col xs={7}>
            <Input
              type="text"
              className="inputLine"
              value={formValues.eventName}
              name="eventName"
              onChange={handleChange}
              maxLength={100}
            />
          </Col>
          <Col xs={2} className="impCol">
            <span className="imp">必須</span>
          </Col>
        </Row>

        <Row className="allRow">
          <Col xs={3}>
            <label className="label">3. イベント詳細</label>
          </Col>
          <Col xs={7}>
            <textarea
              ref={textareaRef}
              rows={1}
              className="autoInput"
              onInput={(e) => {
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }}
              name="eventDetail"
              value={formValues.eventDetail}
              onChange={handleChange}
              maxLength={200}
            />
          </Col>
          <Col xs={2} className="impCol">
            <span className="imp">必須</span>
          </Col>
        </Row>

        <Row className="allRow lastRow">
          <Row className="d-flex justify-content-between px-0 mx-0">
            <Col>
              <label className="label">４. イベント設定</label>
            </Col>
            <Col className="impCol">
              <span className="impLast">必須</span>
            </Col>
          </Row>

          <div className="settingCont">
            <div className="headerRow mt-1">
              <span>釧路マイクログリッド</span>
            </div>
            {renderGroups(
              microGroups,
              setMicroGroups,
              microAllocations,
              microCustomAllocations,
            )}

            <div className="headerRow mt-1">
              <span>釧路役場</span>
            </div>
            {renderGroups(townGroups, setTownGroups, townAllocations)}
          </div>
        </Row>
      </ModalBody>

      <ModalFooter className="footer">
        <Button className="btn cancel" onClick={handleCancel}>
          キャンセル
        </Button>
        <Button
          className="btn add"
          onClick={handleAdd}
          disabled={noData || isAdding || (isEditing && !isChanged)}
        >
          {isAdding ? <Spinner size="sm" /> : isEditing ? "更新" : "追加"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EquipmentModal;
